import { useState, useCallback, useEffect } from 'react'
import { shallow } from 'zustand/shallow'
import CircularProgress from '@mui/material/CircularProgress'
import { debounce } from 'lodash'
import { toast } from 'react-toastify'

import Modal from '@mui/material/Modal'
import Card from '@mui/material/Card'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import { formatNumber3Decimals } from 'src/util/formats'
import { useAirdropState } from 'src/contexts/airDropContext'
import SettingTitle from 'src/components/kit/SettingTitle'
import ModalInputField from 'src/components/kit/ModalInputField'
import UiButton from 'src/ui/atoms/Button'
import { useChainAndNetwork } from 'src/store/chainAndNetwork'
import './CreateModal.scss'

export default function CreateModal({
  showCreateModal,
  toggleCreateModal,
  handleSendTransactions,
  airdrop,
}: {
  showCreateModal: boolean
  toggleCreateModal: () => void
  handleSendTransactions: (data: any) => Promise<void>
  airdrop: any
}) {
  const { chain } = useChainAndNetwork(
    (state) => ({
      chain: state.chain,
    }),
    shallow,
  )
  const [receiver, setReceiver] = useState('')
  const [amount, setAmount] = useState(0)
  const [displayedAmount, setDisplayedAmount] = useState('0')
  const [amountPlacehoder, setAmountPlacehoder] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const airdropState = useAirdropState()

  const debouncedAmount = useCallback(
    debounce((value) => {
      setDisplayedAmount(formatNumber3Decimals(value))
    }, 800),
    [],
  )

  const handleChangeAmount = (value: string) => {
    const val = typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) : parseFloat(value)
    const amount = val && !isNaN(val) && val > 0 ? val : 0

    setAmount(amount)
    setDisplayedAmount(value)
    debouncedAmount(amount)
  }

  const handleCreateTransaction = async () => {
    try {
      if (
        (chain === 'multiversx' &&
          (!receiver?.trim()?.startsWith('erd') || !(receiver?.trim()?.length === 62))) ||
        (chain === 'ethereum' &&
          (!receiver?.trim()?.startsWith('0x') || !(receiver?.trim()?.length === 42)))
      ) {
        toast.error('Invalid bech32 address')
        return
      }
      const parsedData = [
        {
          receiver,
          amount,
          sender: airdropState.initiator,
          status: 'new',
          network: chain,
        },
      ]
      if (airdrop.id) {
        // @ts-ignore
        parsedData[0]['bulkTransactionId'] = airdrop.id
      }
      setIsLoading(true)
      await handleSendTransactions(parsedData)
      setIsLoading(false)
      toggleCreateModal()
    } catch (error) {
      setIsLoading(false)
      console.error(error)
    }
  }

  const handleAmountPlacehoder = () => {
    const airdropChain = airdrop.chain.toLowerCase() || chain

    switch (airdropChain) {
      case 'multiversx':
        return 'e.g. "erd1..."'
      case 'ethereum':
      case 'bnb':
      case 'polygon':
        return 'e.g. "0x..."'
      default:
        return 'e.g. "0x..."'
    }
  }

  useEffect(() => {
    if (!showCreateModal) {
      setReceiver('')
      setAmount(0)
      setDisplayedAmount('0')
    } else {
      setAmountPlacehoder(handleAmountPlacehoder())
    }
  }, [showCreateModal])

  return (
    <Modal
      open={showCreateModal}
      onClose={toggleCreateModal}
      aria-labelledby="create-transaction-modal"
      aria-describedby="create-transaction-modal">
      <Card
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          maxHeight: '90%',
          width: '580px',
          maxWidth: '90%',
          background: '#181D38',
          color: '#fff',
          borderRadius: '16px',
        }}>
        <Box
          py={3}
          px={3}
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            flexDirection: 'column',
            borderRadius: '8px',
            boxSizing: 'border-box',
          }}>
          <Typography
            variant="h5"
            sx={{
              display: 'flex',
              justifyContent: 'center',
              textAlign: 'center',
              marginBottom: '2rem',
              color: '#fff',
              fontFamily: 'SpaceGrotesk, sans-serif',
            }}>
            Create a new transaction
          </Typography>
          <div className="propertyContainer">
            <SettingTitle title="Receiver:" />
            <ModalInputField
              placeholder={amountPlacehoder}
              value={receiver}
              onChange={setReceiver}
            />
          </div>
          <div className="propertyContainer">
            <SettingTitle title="Amount:" />
            <ModalInputField
              placeholder='e.g. "1000"'
              value={displayedAmount}
              onChange={handleChangeAmount}
            />
          </div>
          <Box
            mt={2}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              flexDirection: 'row',
              width: '100%',
              gap: '40px',
            }}>
            <UiButton name="Cancel" size="M" onClick={toggleCreateModal} type="secondary" />
            <Button
              className="btn btn-primary"
              color="success"
              size="medium"
              variant="contained"
              onClick={handleCreateTransaction}
              disabled={isLoading || !receiver || !amount}>
              Create
              {isLoading ? (
                <CircularProgress color="success" size="2rem" sx={{ marginLeft: '1rem' }} />
              ) : null}
            </Button>
          </Box>
        </Box>
      </Card>
    </Modal>
  )
}
